/* eslint-disable no-alert */
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useReducer } from 'react';
import Player from 'components/LitePlayer/Player';
import { excuteAfterLitePlayerReady } from 'components/LitePlayer/helper';
import lazyCss from './MiniTv.scss';
import CloseSticky from '../CloseSticky/CloseSticky';
import {
  getJMinitvConfig,
  getOneTimeLayoutBasedConfig,
  gaSetup,
  setClosecookie,
  updatedLayoutType,
  sharePropsToPlayer,
  getDynLayoutConfig,
  PLAYER_ICONS,
  getInitState,
  reducer,
  onScrollTriggerOnce,
} from '../util';
import DraggableWrapper from '../DraggableWrapper';

let observer = null;
let otherObser = null;
let isLayoutUpdated = false;
let tries = 0;
const maxTries = 25;

// eslint-disable-next-line react/display-name
const Minitv = memo(props => {
  // layoutSsConfig  -> layout override + layout serverside
  // jminiTvData  -> jarvis serverside + jarvis clientside + jarvis override
  const {
    layoutSsConfig,
    processEvents,
    isMobile,
    playerBottomRef,
    globalRefId,
    contentRef,
    updatePlayerState,
  } = props;
  const layoutConfig = getOneTimeLayoutBasedConfig(props);
  const initialState = getInitState(layoutSsConfig, layoutConfig);
  const [state, dispatch] = useReducer(reducer, initialState);

  const { headless } = layoutConfig;
  const jminiTvData = getJMinitvConfig(props, layoutSsConfig, layoutConfig); // jarvis Config
  const { inViewObserver, sticky } = jminiTvData;
  const ref = playerBottomRef?.current;
  const gaSend = gaSetup(props, jminiTvData); // jminiTvData
  const {
    layoutType,
    isDock,
    showTimer,
    overlayBtn,
    play,
    mute,
    showClientUI,
    nocloseBtn,
    isDraggable,
    otControls,
  } = state;

  const updateState = useCallback(
    v => {
      dispatch({
        type: 'update',
        payload: v,
      });

      // Also update parent state
      if (updatePlayerState) {
        updatePlayerState(v);
      }
    },
    [updatePlayerState],
  );

  const isDocked = dock => {
    const ulayout = updatedLayoutType(props, layoutType, dock);
    let obj = { isDock: dock };
    if (ulayout) {
      obj.layoutType = ulayout;
      processEvents('new_layout', {
        ulayout,
        lazyCsslayout: lazyCss[ulayout],
      });
      if (!isLayoutUpdated && ulayout !== layoutType) {
        gaSend(ulayout, 'layout_change_from_dock');
        isLayoutUpdated = true;
      }
      const updatedState = getDynLayoutConfig(ulayout);
      obj = {
        ...obj,
        ...updatedState,
      };
    }
    updateState(obj);
  };

  const globalVarUpdate = (k, v) => {
    if (window?.lwplayerConfig?.minitv) {
      window.lwplayerConfig.minitv[k] = v;
    }
  };

  const onControlsAction = (e, l, v) => {
    try {
      let currentPlayer;

      const findPlayer = () => {
        currentPlayer = window?.lwPlayer && window.lwPlayer[globalRefId];
        if (!currentPlayer) {
          if (tries < maxTries) {
            tries += 1;
            setTimeout(findPlayer, 500 * tries);
          }
          return;
        }

        switch (l) {
          case PLAYER_ICONS[0]:
            currentPlayer.seek(-10, true);
            break;
          case PLAYER_ICONS[2]:
            currentPlayer.pause();
            globalVarUpdate('pauseBy', 'user');
            break;
          case PLAYER_ICONS[1]:
            currentPlayer.play();
            globalVarUpdate('pauseBy', 'removed_user');
            break;
          case PLAYER_ICONS[3]:
            currentPlayer.seek(10, true);
            break;
          case PLAYER_ICONS[4]:
            currentPlayer.unmute();
            break;
          case PLAYER_ICONS[5]:
            currentPlayer.mute();
            break;
          case 'headless':
            currentPlayer.setControlState(!v);
            updateState({ playerControls: !v });
            break;
          default:
            break;
        }
        if (e.preventDefault) {
          e.preventDefault();
        }
      };
      excuteAfterLitePlayerReady(globalRefId, findPlayer);
    } catch (error) {
      //
    }
  };
  const closeDocking = () => {
    onControlsAction({}, PLAYER_ICONS[2], false);
    if (otherObser) {
      otherObser();
    }
    if (observer) {
      isDocked(false);
      observer.unobserve(ref);
      observer.disconnect();
    }
  };

  const processVideoEvent = event => {
    try {
      switch (event) {
        case 'ADSTART':
          updateState({ showTimer: true, overlayBtn: false, play: 1 });
          break;
        case 'ADCOMPLETE':
          updateState({ overlayBtn: true });
          break;
        case 'VIDEOVIEW':
          updateState({ showTimer: true, overlayBtn: true, play: 1 });
          gaSend('view', layoutType);
          break;
        case 'ADSKIP':
          updateState({ overlayBtn: true });
          break;
        case 'timeout_close':
          updateState({ showTimer: true });
          break;
        case 'VIDEOMUTE':
          updateState({ mute: true });
          gaSend('mute', layoutType, 'user_click');
          break;
        case 'VIDEOUNMUTE':
          updateState({ mute: false });
          gaSend('unmute', layoutType, 'user_click');
          break;
        case 'VIDEOPAUSED':
        case 'ADPAUSED':
          updateState({ play: 0 });
          gaSend('pause', layoutType, 'user_click');
          break;
        case 'VIDEORESUMED':
        case 'ADRESUMED':
          updateState({ play: 1 });
          gaSend('resume', layoutType, 'user_click');
          break;
        default:
      }
    } catch (e) {
      //
    }
  };

  useEffect(() => {
    let lazyCssSticky = '';
    if (lazyCss.stickyMinitv && sticky && layoutType !== 'webInpage') {
      lazyCssSticky = ` ${lazyCss.stickyMinitv}`;
    }
    // show minitv
    const showMinitv = () => {
      const { loadEventType, loadeventVal } = jminiTvData;

      updateState({ showClientUI: true, lazyCss });
      processEvents('uiready', {
        lazyCsslayout: lazyCss[layoutType],
        uiReady: 1,
        lazyCssSticky,
      });
      gaSend(layoutType, `${loadEventType}_${loadeventVal}`);
    };
    // Process load type
    const processLoadType = () => {
      const { loadEventType = 'onload', loadeventVal } = jminiTvData;
      let timeoutVal = 10;
      if (loadeventVal > 0) {
        timeoutVal = loadeventVal;
      }
      switch (loadEventType) {
        case 'timeout':
          setTimeout(() => {
            showMinitv(loadEventType);
          }, timeoutVal * 1000);
          break;
        case 'onscroll':
          onScrollTriggerOnce(loadeventVal, () => {
            showMinitv(loadEventType);
          });
          break;
        default:
          showMinitv(loadEventType);
      }
    };
    processLoadType();
    setTimeout(() => {
      processVideoEvent('timeout_close');
    }, 10000);

    const threshold = [0.5, 0];
    const options = {
      root: null, // use the viewport as the root
      rootMargin: '0px',
      threshold, // trigger when 50% of the target is visible
    };
    if (ref && inViewObserver) {
      const callback = entries => {
        entries.forEach(entry => {
          const isOutofViewDownside =
            entry.boundingClientRect.top < entry.intersectionRect.top;
          const notInview = !entry.isIntersecting && isOutofViewDownside;
          isDocked(notInview);
        });
      };
      observer = new IntersectionObserver(callback, options);
      observer.observe(ref);
    }
    const onDocking = e => {
      isDocked(e.isDock);
      otherObser = e.observer;
      onControlsAction({}, 'headless', e.isDock);
    };
    if (window.Times.isGlobalDock) {
      onDocking(window.Times.isGlobalDock);
    }
    window.addEventListener('MINITV_CONTAINER_DOCK', onDocking);

    return () => {
      closeDocking();
    };
  }, []);

  const getkeyWords = () => {
    let { metakeywords } = jminiTvData;
    const { SCN: scn, SubSCN: subscn } = window?.Times?.adsKeys || {};
    metakeywords += scn ? `,${scn}` : '';
    metakeywords += subscn ? `,${subscn}` : '';
    return metakeywords;
  };

  const { divId } = layoutSsConfig;
  const playerkeys = sharePropsToPlayer(props, jminiTvData);

  const liteConfig = {
    ...playerkeys,
    outsideEventListener: args => {
      processVideoEvent(args);
    },
    headless,
    metakeywords: getkeyWords(),
    divId,
    globalRefId,
    skipDiv: true,
  };

  const cond = [
    overlayBtn,
    play === 0,
    play === 1,
    overlayBtn,
    overlayBtn && mute,
    overlayBtn && !mute,
  ];

  const controlUI = otControls && (
    <div className={lazyCss?.otcontrols}>
      {PLAYER_ICONS.map(
        (l, i) =>
          cond[i] && (
            <span
              data-no-drag="1"
              className={lazyCss[l]}
              onClick={e => onControlsAction(e, l)}
            />
          ),
      )}
    </div>
  );
  if (!jminiTvData?.jActive) {
    return null;
  }
  const closeButton = !nocloseBtn &&
    showTimer && (
      <CloseSticky
        timeout={jminiTvData.timeout}
        hide={!isDock}
        parentCss={lazyCss}
        closeMiniTvWidget={() => {
          closeDocking();
          setClosecookie(isDock, isMobile);
          gaSend('close', layoutType, 'user_click');
          processEvents('close_btn');
        }}
      />
    );
  return (
    <>
      {closeButton}
      {showClientUI && controlUI}
      <Player {...liteConfig} />
      <DraggableWrapper
        isDraggable={isDraggable}
        wrapperRef={contentRef}
        updatePlayerState={updatePlayerState}
        animation={
          layoutSsConfig?.layoutType !== layoutType
            ? lazyCss.slideLeftToRight
            : lazyCss.slideLeftToRight
        }
      />
    </>
  );
});

Minitv.propTypes = {
  wrapperProps: PropTypes.shape({}),
  isPrime: false,
  isMobile: true,
  processEvents: PropTypes.func.isRequired,
  jminiTvData: PropTypes.shape({}),
  playerBottomRef: PropTypes.node,
  layoutSsConfig: PropTypes.shape({
    playerClass: PropTypes.string,
    playerStyle: PropTypes.shape({}),
    layoutType: PropTypes.string,
  }).isRequired,
  globalRefId: PropTypes.string,
  contentRef: PropTypes.node,
};

Minitv.defaultProps = {
  wrapperProps: null,
  playerBottomRef: null,
  isPrime: PropTypes.bool,
  isMobile: PropTypes.bool,
  jminiTvData: {
    metakeywords: '',
  },
  globalRefId: 'minitv',
  contentRef: null,
};

export default Minitv;
